import { useState, useEffect, useCallback } from 'react'
import { getPage, post, del } from '../lib/api'
import type { ApiList } from '../lib/api'
import { getToken } from '../lib/auth'

export interface Source {
  id: string
  talentId: string
  kind: 'resume' | 'portfolio' | 'project' | 'jd' | 'memo'
  title: string
  summary: string | null
  keywords: string[]
  r2Key: string | null
  sourceUrl: string | null
  rawPath: string | null
  sizeBytes: number | null
  mimeType: string | null
  contentText: string | null
  createdAt: string
}

const BASE = import.meta.env.VITE_API_BASE ?? ''

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = {}
  const token = getToken()
  if (token) h['Authorization'] = `Bearer ${token}`
  if (import.meta.env.DEV) h['X-Dev-Bypass'] = '1'
  return h
}

export function useSources(talentId: string | null) {
  const [sources, setSources] = useState<Source[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)

  const basePath = talentId ? `/api/v1/talents/${talentId}/sources` : null

  const load = useCallback(async () => {
    if (!basePath) return
    setLoading(true)
    setError(null)
    try {
      const result: ApiList<Source> = await getPage<Source>(basePath)
      setSources(result.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : '알 수 없는 오류')
    } finally {
      setLoading(false)
    }
  }, [basePath])

  useEffect(() => {
    if (!basePath) {
      setSources([])
      return
    }
    load()
  }, [basePath, load])

  const uploadSingle = useCallback(
    async (file: File, meta: { kind: string; title: string; sourceUrl?: string; memo?: string }) => {
      if (!basePath) return
      setError(null)
      setUploading(true)
      setUploadProgress(file.name)
      try {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('kind', meta.kind)
        fd.append('title', meta.title)
        if (meta.sourceUrl) fd.append('sourceUrl', meta.sourceUrl)
        if (meta.memo) fd.append('memo', meta.memo)

        const res = await fetch(`${BASE}${basePath}`, {
          method: 'POST',
          headers: authHeaders(),
          body: fd,
        })
        const json = await res.json()
        if (json.error) throw new Error(json.error.message)
        await load()
      } catch (e) {
        setError(e instanceof Error ? e.message : '업로드 실패')
      } finally {
        setUploading(false)
        setUploadProgress(null)
      }
    },
    [basePath, load],
  )

  const uploadBulk = useCallback(
    async (files: File[], kind: string) => {
      if (!basePath || files.length === 0) return
      setError(null)
      setUploading(true)
      setUploadProgress(`${files.length}개 파일 업로드 중...`)
      try {
        const fd = new FormData()
        fd.append('kind', kind)
        for (const f of files) {
          fd.append('files', f)
        }

        const res = await fetch(`${BASE}${basePath}/bulk`, {
          method: 'POST',
          headers: authHeaders(),
          body: fd,
        })
        const json = await res.json()
        if (json.error) throw new Error(json.error.message)
        setUploadProgress(null)
        await load()
      } catch (e) {
        setError(e instanceof Error ? e.message : '일괄 업로드 실패')
      } finally {
        setUploading(false)
        setUploadProgress(null)
      }
    },
    [basePath, load],
  )

  const remove = useCallback(
    async (id: string) => {
      if (!basePath) return
      setError(null)
      try {
        await del<Source>(`${basePath}/${id}`)
        setSources((prev) => prev.filter((s) => s.id !== id))
      } catch (e) {
        setError(e instanceof Error ? e.message : '삭제 실패')
      }
    },
    [basePath],
  )

  const importFromNotion = useCallback(
    async (pageId: string, kind: string): Promise<boolean> => {
      if (!talentId) return false
      setError(null)
      setUploading(true)
      setUploadProgress('Notion 페이지 가져오는 중...')
      try {
        await post<Source>(`/api/v1/talents/${talentId}/notion/import`, {
          pageId,
          kind,
        })
        await load()
        return true
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Notion 가져오기 실패')
        return false
      } finally {
        setUploading(false)
        setUploadProgress(null)
      }
    },
    [talentId, load],
  )

  return { sources, loading, error, uploading, uploadProgress, reload: load, uploadSingle, uploadBulk, remove, importFromNotion }
}
